/**
 * Production Service - Handle production data via custom proxy
 */

import { ProductionData, ProductionFilters, DailyProductionData, ProductionStats } from '@/types/production';
import { supabase } from '@/integrations/supabase/client';

/** Bearer header with the current Supabase session, required by the authenticated proxy. */
async function authHeader(): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export class ProductionService {
  /**
   * Get production data via our authenticated proxy.
   *
   * NOTE: third-party CORS proxy fallbacks (cors.sh, allorigins) were removed —
   * they tunnelled internal production data through external services.
   */
  async getProductionData(filters: ProductionFilters): Promise<ProductionData[]> {
    const params = new URLSearchParams({
      fromDate: filters.fromDate,
      toDate: filters.toDate,
    });

    if (filters.compID) {
      params.append('compID', filters.compID.toString());
    }

    console.log('🔄 Trying Custom API Proxy...');
    const proxyUrl = `/api/production-data?${params.toString()}`;

    const response = await fetch(proxyUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(await authHeader()),
      },
      // Timeout for custom proxy
      signal: AbortSignal.timeout(220000),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      console.error('❌ Custom API Proxy failed:', response.status, errorData.error || errorData);
      throw new Error('Không thể kết nối đến API sản xuất. Vui lòng kiểm tra kết nối mạng hoặc liên hệ IT support.');
    }

    const data: ProductionData[] = await response.json();
    console.log('✅ Custom API Proxy success:', Array.isArray(data) ? data.length : typeof data, 'records');
    return Array.isArray(data) ? data : [];
  }



  /**
   * Transform raw production data to daily format for display
   */
  transformToDaily(data: ProductionData[]): DailyProductionData[] {
    return data.map(item => ({
      date: item.DocDate,
      processes: {
        soThe: {
          name: 'Sơ chế',
          labor: item.CSChe,
          volume: item.M3SChe,
          productivity: item.NSChe,
        },
        tinhChe: {
          name: 'Tinh chế',
          labor: item.CTChe,
          volume: item.M3TChe,
          productivity: item.NSTChe,
        },
        lapRap: {
          name: 'Lắp ráp',
          labor: item.CLRap,
          volume: item.M3LRap,
          productivity: item.NSLRap,
        },
        nguoi: {
          name: 'Nguội',
          labor: item.CNguoi,
          volume: item.M3Nguoi,
          productivity: item.NSNguoi,
        },
        son: {
          name: 'Sơn',
          labor: item.CPSon,
          volume: item.M3PSon,
          productivity: item.NSPSon,
        },
        kiemCuoi: {
          name: 'Kiểm cuối',
          labor: null, // Chưa có trong API
          volume: null,
          productivity: null,
        },
      },
    }));
  }

  /**
   * Calculate production statistics
   */
  calculateStats(data: ProductionData[]): ProductionStats {
    if (data.length === 0) {
      return {
        totalDays: 0,
        avgProductivity: 0,
        maxVolume: 0,
        totalLabor: 0,
      };
    }

    const totalLabor = data.reduce((sum, item) => {
      const dailyLabor = (item.CSChe || 0) + (item.CTChe || 0) + (item.CLRap || 0) + 
                        (item.CNguoi || 0) + (item.CPSon || 0) + (item.CMass || 0) + (item.CDGoi || 0);
      return sum + dailyLabor;
    }, 0);

    const totalVolume = data.reduce((sum, item) => {
      const dailyVolume = (item.M3SChe || 0) + (item.M3TChe || 0) + (item.M3LRap || 0) + 
                         (item.M3Nguoi || 0) + (item.M3PSon || 0) + (item.M3Mass || 0) + (item.M3DGoi || 0);
      return Math.max(sum, dailyVolume);
    }, 0);

    const productivityValues = data.flatMap(item => [
      item.NSChe, item.NSTChe, item.NSLRap, item.NSNguoi, 
      item.NSPSon, item.NSMass, item.NSDGoi
    ]).filter(val => val !== null && val !== undefined) as number[];

    const avgProductivity = productivityValues.length > 0 
      ? productivityValues.reduce((sum, val) => sum + val, 0) / productivityValues.length 
      : 0;

    return {
      totalDays: data.length,
      avgProductivity: Number(avgProductivity.toFixed(3)),
      maxVolume: Number(totalVolume.toFixed(3)),
      totalLabor: Number(totalLabor.toFixed(1)),
    };
  }

  /**
   * Get chart data for specific process
   */
  getChartData(data: ProductionData[], processType: 'labor' | 'volume' | 'productivity') {
    return data.map(item => {
      const date = new Date(item.DocDate).toLocaleDateString('vi-VN');
      
      const processes = [
        { name: 'Sơ chế', labor: item.CSChe, volume: item.M3SChe, productivity: item.NSChe },
        { name: 'Tinh chế', labor: item.CTChe, volume: item.M3TChe, productivity: item.NSTChe },
        { name: 'Lắp ráp', labor: item.CLRap, volume: item.M3LRap, productivity: item.NSLRap },
        { name: 'Nguội', labor: item.CNguoi, volume: item.M3Nguoi, productivity: item.NSNguoi },
        { name: 'Sơn', labor: item.CPSon, volume: item.M3PSon, productivity: item.NSPSon },
        { name: 'Đóng gói', labor: item.CDGoi, volume: item.M3DGoi, productivity: item.NSDGoi },
      ];

      return processes.map(process => ({
        date,
        value: process[processType] || 0,
        process: process.name,
      }));
    }).flat();
  }
}

// Export singleton instance
export const productionService = new ProductionService();